<script setup lang="ts">
  import { inject, provide } from 'vue';
  import { TableHeaderSection, DefaultTable, TableLoading, SearchNoResults } from '@/shared/ui';
  import type { OrderOptions } from '@/shared/lib';
  import { RefreshDocumentListKey } from '@/shared/lib/sortOrder';

  import { HEADERS } from '@/features/Document';

  import type { DocumentRevision, FilterOptions } from '@/entities/Document';

  import ListTableRow from './ListTableRow.vue';
  import { useDocumentListTable } from '../model/useDocumentListTable';
  import { useDocumentRevisionGroups } from '../model/useDocumentRevisionGroups';

  const headersWithExpansion = [...HEADERS, ''];

  interface Props {
    revisions: DocumentRevision[];
    isFilterActive?: boolean;
    orderOptions?: OrderOptions[];
    loading?: boolean;
  }

  const props = withDefaults(defineProps<Props>(), {
    revisions: () => [],
    isFilterActive: false,
    orderOptions: () => [],
    loading: false,
  });

  const emit = defineEmits<{
    delete: [deletedIds: number[]];
    replace: [revision: DocumentRevision];
    'update:sort': [sortCamp: string, orderFlow: string];
    'update:filter': [filters: FilterOptions];
    'filter-clicked': [];
  }>();

  const {
    groupedRevisions,
    latestRevisions,
    expandedDocumentId,
    handleToggleExpand,
    isDocumentExpanded,
    getPreviousRevisionsState,
    retryLoadPreviousRevisions,
    handleRevisionsDeleted,
    invalidatePreviousRevisions,
    invalidateAllPreviousRevisions,
  } = useDocumentRevisionGroups({
    revisions: () => props.revisions,
  });

  defineExpose({
    expandedDocumentId,
    handleToggleExpand,
  });
  
  const ancestorRefreshDocumentList = inject(RefreshDocumentListKey, undefined);
  const refreshDocumentList = () => {
    const expandedGroup =
      expandedDocumentId.value !== null
        ? groupedRevisions.value.find((group) => group.documentId === expandedDocumentId.value)
        : undefined;

    invalidateAllPreviousRevisions();

    if (expandedGroup) {
      retryLoadPreviousRevisions(expandedGroup.documentId, expandedGroup.latestRevision.id);
    }

    ancestorRefreshDocumentList?.();
  };
  provide(RefreshDocumentListKey, refreshDocumentList);

  const {
    selectedCount,
    hasSelectedDocuments,
    isDocumentSelected,
    selectedDocuments,
    handleToggleSelection,
    handleToggleAll,
    allSelected,
    handleSortOptions,
    handleFilter,
    handleFilterClicked,
    orderOptions,
    handleOpenDeleteConfirmation,
    handleSingleDelete,
  } = useDocumentListTable({
    revisions: () => latestRevisions.value,
    onDelete: (deletedIds) => {
      handleRevisionsDeleted(deletedIds);
      emit('delete', deletedIds);
    },
    onSort: (sortCamp, orderFlow) => emit('update:sort', sortCamp, orderFlow),
    onFilter: (filters) => emit('update:filter', filters),
    onFilterClicked: () => emit('filter-clicked'),
  });

  const handleReplace = (documentId: number, revision: DocumentRevision) => {
    invalidatePreviousRevisions(documentId);
    emit('replace', revision);
  };
</script>

<template>
  <TableLoading
    v-if="props.loading"
    :row-size="11"
    :column-size="headersWithExpansion.length + 1"
  />
  <DefaultTable
    v-else
    class="divide-y divide-gray-02 min-w-full"
  >
    <TableHeaderSection
      :headers="headersWithExpansion"
      :item-count="groupedRevisions.length"
      :show-column-headers="groupedRevisions.length > 0"
      title="Lista de Documentos"
      item-type="documentos"
      :has-selected="hasSelectedDocuments"
      :selected-count="selectedCount"
      :selected-documents="selectedDocuments"
      :all-selected="allSelected"
      :order-options="orderOptions"
      :is-filter-active="props.isFilterActive"
      @toggle-all="handleToggleAll"
      @update:sort="handleSortOptions"
      @update:filter="handleFilter"
      @filter-clicked="handleFilterClicked"
      @delete="handleOpenDeleteConfirmation"
    />
    <tbody class="bg-white-01 divide-y divide-gray-02 border-t border-gray-02">
      <tr v-if="!groupedRevisions.length">
        <td
          :colspan="headersWithExpansion.length + 1"
          class="py-8"
        >
          <SearchNoResults />
        </td>
      </tr>
      <template
        v-for="group in groupedRevisions"
        :key="group.documentId"
      >
        <ListTableRow
          :revision="group.latestRevision"
          :is-selected="isDocumentSelected(group.latestRevision.id)"
          :has-older-revisions="group.hasOlderRevisions"
          :is-expanded="isDocumentExpanded(group.documentId)"
          @toggle-expand="handleToggleExpand(group.documentId, group.latestRevision.id)"
          @toggle-selection="handleToggleSelection"
          @delete="handleSingleDelete"
          @replace="handleReplace(group.documentId, $event)"
        />
        <tr
          v-if="
            isDocumentExpanded(group.documentId) &&
            getPreviousRevisionsState(group.documentId).status === 'loading'
          "
          :data-testid="`document-revisions-loading-${group.documentId}`"
        >
          <td
            :colspan="headersWithExpansion.length + 1"
            class="py-4 text-center text-sm text-gray-06"
          >
            Carregando revisões anteriores...
          </td>
        </tr>
        <tr
          v-if="
            isDocumentExpanded(group.documentId) &&
            getPreviousRevisionsState(group.documentId).status === 'error'
          "
          :data-testid="`document-revisions-error-${group.documentId}`"
        >
          <td
            :colspan="headersWithExpansion.length + 1"
            class="py-4 text-center text-sm text-red-600"
          >
            Não foi possível carregar as revisões anteriores.
            <button
              type="button"
              class="ml-2 underline"
              @click="retryLoadPreviousRevisions(group.documentId, group.latestRevision.id)"
            >
              Tentar novamente
            </button>
          </td>
        </tr>
        <ListTableRow
          v-for="olderRevision in group.olderRevisions"
          :key="olderRevision.id"
          :revision="olderRevision"
          :is-selected="isDocumentSelected(olderRevision.id)"
          :hidden="!isDocumentExpanded(group.documentId)"
          is-child
          @toggle-selection="handleToggleSelection"
          @delete="handleSingleDelete"
          @replace="handleReplace(group.documentId, $event)"
        />
      </template>
    </tbody>
  </DefaultTable>
</template>
